import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingUp, AlertTriangle, Database, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { ExtremePriceGougingTable } from "./ExtremePriceGougingTable";
import { codeFamilyColor } from '@/lib/codeFamilies';

// Colors come from the one shared code-family palette (src/lib/codeFamilies.ts)
const CODE_TYPE_COLORS: Record<string, { color: string; name: string; }> = {
  'CPT': { color: codeFamilyColor('CPT'), name: 'CPT Codes' },
  'DRG': { color: codeFamilyColor('DRG'), name: 'DRG Codes' },
  'HCPCS': { color: codeFamilyColor('HCPCS'), name: 'HCPCS Codes' },
  'REV': { color: codeFamilyColor('REV'), name: 'Revenue Codes' },
  'NDC': { color: codeFamilyColor('NDC'), name: 'Drug Codes (NDC)' },
  'SUPPLY': { color: codeFamilyColor('SUPPLY'), name: 'Supply Codes' },
};

export const PricingAnalyticsSummary = () => {
  // Query analytics summary from cached materialized view
  const { data: analyticsSummary, isLoading } = useQuery({
    queryKey: ['pricing-analytics-summary-homepage'],
    staleTime: Infinity, // Cache indefinitely - only cleared manually
    gcTime: Infinity, // Keep in cache forever
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      // Query the materialized view - single fast query instead of 16 separate queries
      const { data, error } = await supabase
        .from('mv_analytics_summary')
        .select('*')
        .single();

      if (error) throw error;

      return {
        cptCount: data.cpt_count || 0,
        drgCount: data.drg_count || 0,
        hcpcsCount: data.hcpcs_count || 0,
        revenueCount: data.revenue_count || 0,
        ndcCount: (data.ndc_count || 0) + (data.drug_count || 0), // Combine NDC and drug counts
        supplyCount: data.supply_count || 0,
        hospitalAnalytics: data.hospital_analytics || 0,
        hospitalRankings: data.hospital_rankings || 0,
        // Unique code definitions by type
        uniqueCptCodes: data.unique_cpt_codes || 0,
        uniqueHcpcsCodes: data.unique_hcpcs_codes || 0,
        uniqueRevCodes: data.unique_rev_codes || 0,
        uniqueDrgCodes: data.unique_drg_codes || 0,
        uniqueDrugCodes: data.unique_drug_codes || 0,
        uniqueSupplyCodes: data.unique_supply_codes || 0,
      };
    }
  });

  // Check if we have analytics data to show the table
  const hasAnalyticsData = analyticsSummary?.hospitalAnalytics && analyticsSummary.hospitalAnalytics > 0;

  if (isLoading) {
    return (
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-secondary/20 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-matrix-green mb-3 sm:mb-4">
              Real-Time Analytics
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
              Loading pricing intelligence data...
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-muted/20 animate-pulse">
                <CardContent className="p-4">
                  <div className="h-6 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded mb-1"></div>
                  <div className="h-3 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <Card key={i} className="bg-muted/20 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-8 bg-muted rounded mb-4"></div>
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-3 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const hasData = analyticsSummary && (
    analyticsSummary.cptCount > 0 ||
    analyticsSummary.drgCount > 0 ||
    analyticsSummary.hcpcsCount > 0 ||
    analyticsSummary.revenueCount > 0 ||
    analyticsSummary.ndcCount > 0 ||
    analyticsSummary.supplyCount > 0 ||
    analyticsSummary.hospitalAnalytics > 0 ||
    analyticsSummary.hospitalRankings > 0
  );

  if (!hasData) {
    return (
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-secondary/20 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-matrix-green mb-3 sm:mb-4">
              Real-Time Analytics
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
              Pricing intelligence system initializing...
            </p>
          </div>
          
          <Card className="max-w-2xl mx-auto bg-muted/10 border-warning/20">
            <CardContent className="p-8 text-center">
              <RefreshCw className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Analytics Initializing</h3>
              <p className="text-muted-foreground mb-4">
                The pricing analytics engine is warming up. Visit the admin panel to start data processing.
              </p>
              <Link to="/admin" className="inline-flex items-center text-primary hover:text-primary/80 font-medium">
                Initialize Analytics
                <BarChart3 className="w-4 h-4 ml-1" />
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-secondary/20 z-10">
      <div className="mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-matrix-green mb-3 sm:mb-4">
            Real-Time Analytics
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
            Live pricing intelligence calculated across all medical code types
          </p>
        </div>

        {/* Code Type Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {/* CPT Codes */}
          <Link to="/cpt" className="cursor-pointer">
            <Card className="bg-black/60 border-[#60a5fa]/30 hover:border-[#60a5fa]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Database className="w-5 h-5" style={{ color: '#60a5fa' }} />
                  <Badge variant="outline" style={{ color: '#60a5fa', borderColor: '#60a5fa40' }} className="text-[10px]">
                    CPT
                  </Badge>
                </div>
                <div className="text-xl font-bold font-mono mb-1" style={{ color: '#60a5fa' }}>
                  {analyticsSummary.cptCount.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  Aggregations
                </div>
                <div className="text-sm font-mono" style={{ color: '#60a5fa80' }}>
                  {analyticsSummary.uniqueCptCodes.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Unique Codes
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* DRG Codes */}
          <Link to="/drg" className="cursor-pointer">
            <Card className="bg-black/60 border-[#c084fc]/30 hover:border-[#c084fc]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Database className="w-5 h-5" style={{ color: '#c084fc' }} />
                  <Badge variant="outline" style={{ color: '#c084fc', borderColor: '#c084fc40' }} className="text-[10px]">
                    DRG
                  </Badge>
                </div>
                <div className="text-xl font-bold font-mono mb-1" style={{ color: '#c084fc' }}>
                  {analyticsSummary.drgCount.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  Aggregations
                </div>
                <div className="text-sm font-mono" style={{ color: '#c084fc80' }}>
                  {analyticsSummary.uniqueDrgCodes.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Unique Codes
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* HCPCS Codes */}
          <Link to="/hcpcs" className="cursor-pointer">
            <Card className="bg-black/60 border-[#4ade80]/30 hover:border-[#4ade80]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Database className="w-5 h-5" style={{ color: '#4ade80' }} />
                  <Badge variant="outline" style={{ color: '#4ade80', borderColor: '#4ade8040' }} className="text-[10px]">
                    HCPCS
                  </Badge>
                </div>
                <div className="text-xl font-bold font-mono mb-1" style={{ color: '#4ade80' }}>
                  {analyticsSummary.hcpcsCount.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  Aggregations
                </div>
                <div className="text-sm font-mono" style={{ color: '#4ade8080' }}>
                  {analyticsSummary.uniqueHcpcsCodes.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Unique Codes
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Revenue Codes */}
          <Link to="/revenue" className="cursor-pointer">
            <Card className="bg-black/60 border-[#facc15]/30 hover:border-[#facc15]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Database className="w-5 h-5" style={{ color: '#facc15' }} />
                  <Badge variant="outline" style={{ color: '#facc15', borderColor: '#facc1540' }} className="text-[10px]">
                    REV
                  </Badge>
                </div>
                <div className="text-xl font-bold font-mono mb-1" style={{ color: '#facc15' }}>
                  {analyticsSummary.revenueCount.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  Aggregations
                </div>
                <div className="text-sm font-mono" style={{ color: '#facc1580' }}>
                  {analyticsSummary.uniqueRevCodes.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Unique Codes
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Drug Codes (NDC) */}
          <Link to="/drugs" className="cursor-pointer">
            <Card className="bg-black/60 border-[#fb7185]/30 hover:border-[#fb7185]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Database className="w-5 h-5" style={{ color: '#fb7185' }} />
                  <Badge variant="outline" style={{ color: '#fb7185', borderColor: '#fb718540' }} className="text-[10px]">
                    NDC
                  </Badge>
                </div>
                <div className="text-xl font-bold font-mono mb-1" style={{ color: '#fb7185' }}>
                  {analyticsSummary.ndcCount.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  Aggregations
                </div>
                <div className="text-sm font-mono" style={{ color: '#fb718580' }}>
                  {analyticsSummary.uniqueDrugCodes.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Unique Codes
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Supply Codes */}
          <Link to="/supply" className="cursor-pointer">
            <Card className="bg-black/60 border-[#fb923c]/30 hover:border-[#fb923c]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Database className="w-5 h-5" style={{ color: '#fb923c' }} />
                  <Badge variant="outline" style={{ color: '#fb923c', borderColor: '#fb923c40' }} className="text-[10px]">
                    SUPPLY
                  </Badge>
                </div>
                <div className="text-xl font-bold font-mono mb-1" style={{ color: '#fb923c' }}>
                  {analyticsSummary.supplyCount.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  Aggregations
                </div>
                <div className="text-sm font-mono" style={{ color: '#fb923c80' }}>
                  {analyticsSummary.uniqueSupplyCodes.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Unique Codes
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Summary Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-black/60 border-matrix-green/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-8 h-8 text-green-400" />
                <Badge variant="outline" className="text-green-400 border-green-400/30">
                  Live
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-green-400 mb-1">
                {analyticsSummary.hospitalAnalytics.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                Hospital comparisons active
              </div>
              <div className="text-xs text-green-400/70 mt-2">
                Individual vs national pricing
              </div>
            </CardContent>
          </Card>

          <Card className="bg-black/60 border-matrix-green/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-8 h-8 text-red-400" />
                <Badge variant="outline" className="text-red-400 border-red-400/30">
                  Alert
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-red-400 mb-1">
                {analyticsSummary.hospitalRankings.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                Hospitals ranked by outliers
              </div>
              <div className="text-xs text-red-400/70 mt-2">
                Price transparency scores
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Extreme Price Gouging Table - Full Width */}
      {hasAnalyticsData && (
        <div className="w-full px-2 sm:px-4 lg:px-5">
          <ExtremePriceGougingTable />
        </div>
      )}
    </section>
  );
};